import React from 'react';
import { Box, Stack, Typography, styled } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Button } from 'components/Button/Button';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';

export interface ReadingRow {
  id: number | string;
  value: number | string;
  unit?: string;
  /** Pre-formatted date, e.g. "May 3, 2026". */
  dateLabel: string;
  /** Second line under the value, e.g. a meal relationship. */
  label?: string;
  /** Second line under the date, e.g. "9:05 AM". */
  time?: string;
  /** Leading marker, e.g. a chart-series swatch. */
  marker?: React.ReactNode;
}

const HeaderBar = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1.5, 2.5),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2, 4.25, 2, 3.5),
  },
}));

// primary.main on the tinted page background is 4.24:1 at 20px (axe needs 4.5:1); brandShade1 passes.
const Title = styled(Typography)(({ theme }) => ({
  color: theme.palette.brandShade1.main,
  ...theme.typography.callout,
  fontWeight: theme.typography.fontWeightMedium,
  [theme.breakpoints.up('sm')]: {
    ...theme.typography.title3,
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

const DownloadButton = styled(Button)(({ theme }) => ({
  ...theme.typography.callout,
  height: 40,
  paddingInline: theme.spacing(2),
  lineHeight: 1.3125,
  [theme.breakpoints.up('sm')]: {
    ...theme.typography.headline,
    height: 45,
    paddingInline: theme.spacing(2.75),
    lineHeight: 1.3333,
  },
}));

const Rows = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
}));

// Figma: marker sits 19px (mobile) / 31px (tablet, desktop) from the left, date 23px / 40px from
// the right.
const Row = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 3, 1.5, 2.25),
  borderBottom: `1px solid ${theme.palette.grey[400]}`,
  color: theme.palette.grey[800],
  '&:last-child': { borderBottom: 'none' },
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2, 5, 2, 3.75),
  },
}));

const RowStart = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  [theme.breakpoints.up('sm')]: { gap: theme.spacing(2.75) },
}));

const Marker = styled(Box)({ display: 'flex' });

// Figma: title3/medium on mobile, title1/medium on tablet/desktop; the unit stays regular.
const Value = styled(Typography)(({ theme }) => ({
  ...theme.typography.title3,
  fontWeight: theme.typography.fontWeightMedium,
  [theme.breakpoints.up('sm')]: {
    ...theme.typography.title1,
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

const Unit = styled('span')(({ theme }) => ({
  ...theme.typography.subheadline,
  [theme.breakpoints.up('sm')]: { ...theme.typography.callout },
}));

const Label = styled(Typography)(({ theme }) => ({
  ...theme.typography.subheadline,
  [theme.breakpoints.up('sm')]: { ...theme.typography.callout },
}));

const DateColumn = styled(Stack)({ alignItems: 'flex-end' });

const DateText = styled(Typography)(({ theme }) => ({
  ...theme.typography.subheadline,
  lineHeight: '20px',
  [theme.breakpoints.up('sm')]: { ...theme.typography.title3, lineHeight: '25px' },
}));

export interface WidgetReadingListProps {
  /** Overrides the translated "Past Readings" title. */
  title?: string;
  readings: ReadingRow[];
  /** The Download button shows only when this is passed. */
  onDownload?: () => void;
  isDownloading?: boolean;
  className?: string;
}

// The "Past Readings" title + Download bar and the list of readings under it, shared by every
// widget detail page (Blood Sugar, Weight, Blood Pressure, Step Count -- same layout in Figma).
export const WidgetReadingList = ({
  title,
  readings,
  onDownload,
  isDownloading = false,
  className,
}: WidgetReadingListProps) => {
  const { translation } = useTranslationContext();

  return (
    <Box className={className}>
      <HeaderBar>
        <Title>{title ?? translation('past_readings')}</Title>
        {onDownload && (
          <DownloadButton
            variant="tertiary"
            startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
            onClick={onDownload}
            disabled={isDownloading}
          >
            {translation('download')}
          </DownloadButton>
        )}
      </HeaderBar>
      <Rows>
        {readings.map((reading) => (
          <Row key={reading.id}>
            <RowStart>
              {reading.marker && <Marker>{reading.marker}</Marker>}
              <Stack>
                <Value>
                  {reading.value} {reading.unit && <Unit>{reading.unit}</Unit>}
                </Value>
                {reading.label && <Label>{reading.label}</Label>}
              </Stack>
            </RowStart>
            <DateColumn>
              <DateText>{reading.dateLabel}</DateText>
              {reading.time && <DateText>{reading.time}</DateText>}
            </DateColumn>
          </Row>
        ))}
      </Rows>
    </Box>
  );
};
